/**
 * Client-side insurance API service.
 *
 * Talks ONLY to our own Netlify edge-function proxies (/api/insurance-*),
 * never to battleface directly — the JWT stays server-side.
 *
 * Response mapping (mapQuote / mapOrder) normalises battleface's payload into
 * a small, stable shape the UI uses. VERIFY the field reads marked `// VERIFY`
 * against the pasted Postman response examples — this file plus
 * netlify/edge-functions/_battleface.js are the only spots to touch.
 */

async function postJson(path, body) {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const message = data?.error || `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.details = data?.details;
    throw err;
  }
  return data;
}

/* ── Normalisers ──────────────────────────────────────────────────────── */

export function mapQuote(raw) {
  // battleface wraps the payload in { data: {...} }; our edge fn returns { quote: data }.
  const q = raw?.quote?.data ?? raw?.quote ?? raw?.data ?? raw ?? {};
  return {
    quoteId: q.id ?? q._id ?? q.quote_id ?? q.quoteId,
    quoteNumber: q.quote_number ?? q.quoteNumber,
    premium: q.premium ?? q.total_premium ?? q.price,
    currency: q.currency ?? null,
    benefits: q.benefits ?? q.coverages ?? [],
    raw: q,
  };
}

export function mapOrder(raw) {
  const o = raw?.order?.data ?? raw?.order ?? raw?.data ?? raw ?? {};
  return {
    orderId: o.id ?? o._id ?? o.order_id ?? o.orderId,
    orderNumber: o.order_number ?? o.orderNumber,
    policyNumber: o.policy_number ?? o.policyNumber ?? o.order_number,
    status: o.status, // "Pending" | "Approved" | "Cancelled"
    premium: o.premium ?? o.total_premium ?? o.price,
    currency: o.currency ?? null,
    documents: o.documents ?? o.policy_documents ?? [],
    raw: o,
  };
}

/* ── Flow (Quote → Order → Payment) ───────────────────────────────────── */

export async function getQuote(input) {
  return mapQuote(await postJson("/api/insurance-quote", input));
}

export async function createOrder(input) {
  return mapOrder(await postJson("/api/insurance-order", input));
}

export async function processPayment(input) {
  return mapOrder(await postJson("/api/insurance-payment", input));
}

/* ── Cancellation ─────────────────────────────────────────────────────── */

export async function getCancellationPreview(orderId, body) {
  return postJson("/api/insurance-cancel-preview", { orderId, body });
}

export async function confirmCancellation(orderId, body) {
  return mapOrder(await postJson("/api/insurance-cancel-confirm", { orderId, body }));
}

/* ── Modification ─────────────────────────────────────────────────────── */

export async function getModifiableFields(orderId) {
  return postJson("/api/insurance-modify-fields", { orderId });
}

export async function previewModification(orderId, changes) {
  return postJson("/api/insurance-modify-preview", { orderId, changes });
}

export async function updateModification(orderId, changes) {
  return mapOrder(await postJson("/api/insurance-modify-update", { orderId, changes }));
}
